import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Controller, useForm } from 'react-hook-form';
import { LogCategory } from '../../types/models/SystemLog';
import { DataGrid } from '@mui/x-data-grid';
import { useState } from 'react';
import { useSetupSystemLogTable } from './hooks/useSetupSystemLogTable';
import { endOfDay, startOfDay } from 'date-fns';
import { SystemLogFilters } from './types';
import PurgeSystemLogsDialog from './PurgeSystemLogsDialog';
import { getSystemLogExport } from './api/getSystemLogExport';
import { saveAs } from 'file-saver';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { Box } from '@mui/system';

const SystemLogPage = () => {
  const [purgeOpen, setPurgeOpen] = useState(false);
  const { timezone, dateFormat, timeFormat, dateFormatWithZeros } =
    useUserDateTime();
  const { control, watch, getValues } = useForm<SystemLogFilters>({
    defaultValues: {
      fromDate: new Date(),
      toDate: new Date(),
      categoryId: -1,
    },
  });

  const { columns, rows } = useSetupSystemLogTable(
    watch('categoryId'),
    zonedTimeToUtc(startOfDay(watch('fromDate')), timezone).toISOString(),
    zonedTimeToUtc(endOfDay(watch('toDate')), timezone).toISOString(),
    timezone,
    `${dateFormat} ${timeFormat}`
  );

  const handleExport = async () => {
    const form = getValues();
    const fileName = `spectras-system-log-export_${formatInTimeZone(
      new Date(),
      timezone,
      'yyyy-MM-dd_hh-mm-ss_aa'
    )}.xlsx`;
    const exportedFile = await getSystemLogExport(
      form.categoryId,
      zonedTimeToUtc(startOfDay(form.fromDate), timezone).toISOString(),
      zonedTimeToUtc(endOfDay(form.toDate), timezone).toISOString()
    );
    if (exportedFile) saveAs(exportedFile, fileName);
  };

  return (
    <Grid direction={'column'} container spacing={2}>
      <PurgeSystemLogsDialog
        open={purgeOpen}
        handleClose={() => setPurgeOpen(false)}
      />
      <Grid item container spacing={1}>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='fromDate'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='From'
                  maxDate={watch('toDate')}
                  inputFormat={dateFormatWithZeros}
                  value={value}
                  onChange={onChange}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='toDate'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='To'
                  minDate={watch('fromDate')}
                  inputFormat={dateFormatWithZeros}
                  value={value}
                  onChange={onChange}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item style={{ width: '15rem' }}>
          <Controller
            name='categoryId'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  labelId='category'
                  value={value}
                  onChange={onChange}
                  label='Category'
                >
                  <MenuItem key={-1} value={-1}>
                    All
                  </MenuItem>
                  {Object.values(LogCategory)
                    .filter((categoryNumber) => !isNaN(Number(categoryNumber)))
                    .map((categoryNumber) => (
                      <MenuItem key={categoryNumber} value={categoryNumber}>
                        {LogCategory[Number(categoryNumber)]}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid
          item
          container
          xs
          alignItems='center'
          justifyContent={'flex-end'}
          spacing={1}
        >
          <Grid item>
            <Button variant={'contained'} onClick={() => handleExport()}>
              Export
            </Button>
          </Grid>
          <Grid item>
            <Button variant={'contained'} onClick={() => setPurgeOpen(true)}>
              Purge
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container>
        <Box
          sx={{
            width: '100%',
            paddingBottom: '2rem',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: '10px 10px 0px 0px',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: '0px 0px 10px 10px',
            },
            '& .tableRow': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-root': {
              borderRadius: '10px',
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            autoHeight
            pageSize={13}
            getRowClassName={() => 'tableRow'}
          ></DataGrid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SystemLogPage;
